<template>
  <RightSheet style="width: 90vw;" v-model="dialogVisible" max-width="1920" class="fire-detail-dialog">
    <div class="dialog-wrapper">
      <!-- 标题 -->
      <div class="dialog-title">消防系统详情</div>

      <!-- 主内容区域 -->
      <div class="dialog-main">
        <!-- 左侧：消防数据内容区 -->
        <div class="left-content">
          <div v-if="loading" class="d-flex justify-center align-center" style="height: 100%">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="!fireData || fireData.length === 0" class="empty-text">
            暂无数据
          </div>
          <div v-else class="fire-list">
            <!-- 遍历 eLink (消防方阵) -->
            <div v-for="(elink, index) in fireData" :key="elink.elink_id || index" class="fire-array-panel"
              :id="'fire-array-' + (elink.elink_id ?? index)">

              <!-- 折叠面板标题栏 -->
              <div class="array-header" @click="togglePanel(index)">
                <div class="array-title">{{ index + 1 }}#储能方阵</div>
                <div class="header-right">
                  <span class="header-time" v-if="elink.data?.record_time">
                    采样时间: {{ formatTime(elink.data.record_time) }}
                  </span>
                  <img src="@/assets/images/station-monitor/detail-dialog/collapse-icon.png" class="collapse-icon"
                    :class="{ 'expanded': !collapsedPanels[index] }" alt="展开/收起" />
                </div>
              </div>

              <!-- 展开内容 -->
              <transition name="panel-expand">
                <div v-show="!collapsedPanels[index]" class="array-content-wrapper">
                  <div class="array-content">

                    <!-- 左侧：eLink 信息卡片 -->
                    <div class="elink-info-card"
                      :class="{ 'highlight-device': highlightedDevice.type === 'eLink' && highlightedDevice.id == (elink.uuid ?? index) }"
                      :data-device-id="elink.uuid">
                      <!-- 头部 -->
                      <div class="elink-header">
                        <div class="elink-icon-title">
                          <!-- 图标可以用图片或者 mdi-icon，这里用渐变色块模拟截图 -->
                          <div class="header-icon-box">
                            <img src="@/assets/images/station-monitor/detail-dialog/panel-header-icon.png" width="14"
                              height="14" />
                          </div>
                          <span class="elink-name">{{ elink.elink_name }}</span>
                        </div>
                      </div>

                      <!-- 信息列表 (两列布局) -->
                      <div class="elink-info-grid">
                        <div class="info-item-box">
                          <div class="label">方阵消防预警级别</div>
                          <div class="value">
                            <v-icon size="small" color="#FF7D00" class="mr-1">mdi-alarm-light-outline</v-icon>
                            <span class="text-value">{{
                              getAlarmLevelDesc(elink.data.matrix?.alarm_level) }}</span>
                          </div>
                        </div>

                        <div class="info-item-box">
                          <div class="label">消防主机在线状态</div>
                          <div class="value">
                            <div class="status-dot mr-1"
                              :class="elink.data.matrix?.offline === 1 ? 'success' : 'normal'"></div>
                            <span class="text-value">{{ getOfflineDesc(elink.data.matrix?.offline) }}</span>
                          </div>
                        </div>

                        <div class="info-item-box">
                          <div class="label">消防主机供电方式</div>
                          <div class="value">
                            <v-icon size="small" :color="elink.data.matrix?.power_supply === 1 ? '#FF4D4F' : '#52C41A'"
                              class="mr-1">mdi-lightning-bolt</v-icon>
                            <span class="text-value">{{ getPowerSupplyDesc(elink.data.matrix?.power_supply) }}</span>
                          </div>
                        </div>

                        <!-- 占位，保持布局对齐，或者留空 -->
                        <div class="info-item-box empty-box"></div>

                        <div class="info-item-box">
                          <div class="label">主机电池状态</div>
                          <div class="value">
                            <v-icon size="small" :color="elink.data.matrix?.bat_low === 1 ? '#FF4D4F' : '#52C41A'"
                              class="mr-1">mdi-battery-high</v-icon>
                            <span class="text-value">{{ getBatLowDesc(elink.data.matrix?.bat_low) }}</span>
                          </div>
                        </div>

                        <div class="info-item-box">
                          <div class="label">主机液位状态</div>
                          <div class="value">
                            <v-icon size="small" :color="elink.data.matrix?.liquid_low === 1 ? '#FF4D4F' : '#1890FF'"
                              class="mr-1">mdi-water-outline</v-icon>
                            <span class="text-value">{{ getLiquidLowDesc(elink.data.matrix?.liquid_low) }}</span>
                          </div>
                        </div>

                        <div class="info-item-box">
                          <div class="label">主机喷洒状态</div>
                          <div class="value">
                            <v-icon size="small" :color="elink.data.matrix?.spraying === 1 ? '#52C41A' : '#999'"
                              class="mr-1">mdi-sprinkler-variant</v-icon>
                            <span class="text-value">{{ getSprayingDesc(elink.data.matrix?.spraying) }}</span>
                          </div>
                        </div>

                        <!-- 占位 -->
                        <div class="info-item-box empty-box"></div>

                      </div>

                      <!-- 底部版本信息 -->
                      <div class="version-info mt-4">
                        <div class="version-row">
                          <span class="version-label">消防主机通信协议版本号</span>
                          <span class="version-value">{{ elink.data.matrix?.protocol_ver || '--'
                          }}</span>
                        </div>
                        <div class="version-row">
                          <span class="version-label">消防主机程序版本号</span>
                          <span class="version-value">{{ elink.data.matrix?.soft_ver || '--'
                          }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- 右侧：eBlock 表格 -->
                    <div class="eblock-table-container" style="position: relative;">
                      <v-table density="compact" hover class="custom-table" fixed-header height="100%">
                        <thead class="table-header">
                          <tr>
                            <th class="text-left fixed-col" style="width: 100px;">eBlock</th>
                            <th class="text-left" style="min-width: 120px;">柜体消防预警级别</th>
                            <th class="text-left">
                              <div style="display: flex; align-items: center;" class="header-pointer"
                                @click="openChartDialog(elink, 'temp')">
                                柜体温度
                                <img src="@/assets/images/station-monitor/detail-dialog/header-chart-icon.svg"
                                  width="19" height="13" class="ml-1" alt="" />
                              </div>
                            </th>
                            <th class="text-left">
                              <div style="display: flex; align-items: center;" class="header-pointer"
                                @click="openChartDialog(elink, 'hum')">
                                柜体湿度
                                <img src="@/assets/images/station-monitor/detail-dialog/header-chart-icon.svg"
                                  width="19" height="13" class="ml-1" alt="" />
                              </div>
                            </th>
                            <th class="text-left">
                              <div style="display: flex; align-items: center;" class="header-pointer"
                                @click="openChartDialog(elink, 'co_conc')">
                                CO浓度
                                <img src="@/assets/images/station-monitor/detail-dialog/header-chart-icon.svg"
                                  width="19" height="13" class="ml-1" alt="" />
                              </div>
                            </th>
                            <th class="text-left">
                              <div style="display: flex; align-items: center;" class="header-pointer"
                                @click="openChartDialog(elink, 'smoke_conc')">
                                烟雾浓度
                                <img src="@/assets/images/station-monitor/detail-dialog/header-chart-icon.svg"
                                  width="19" height="13" class="ml-1" alt="" />
                              </div>
                            </th>
                            <th class="text-left">CO状态</th>
                            <th class="text-left">VOC状态</th>
                            <th class="text-left">柜内主机喷洒状态</th>
                            <th class="text-left">消防中继手动报警</th>
                            <th class="text-left">消防中继手动急停</th>
                            <th class="text-left">消防中继程序版本号</th>
                            <th class="text-left">消防中继H2浓度</th>
                            <th class="text-left">H2状态</th>
                            <th class="text-left">消防中继在线状态</th>
                            <th class="text-center fixed-col-right" style="width: 100px;">Pack</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="!elink.data.cabinet || elink.data.cabinet.length === 0">
                          </tr>
                          <tr v-else v-for="block in elink.data.cabinet" :key="block.index"
                            :class="{ 'highlight-row': highlightedDevice.type === 'eBlock' && highlightedDevice.id == block.uuid && highlightedDevice.elinkId == (elink.uuid) }"
                            :data-index="block.index" :data-device-id="block.uuid">
                            <td class="fixed-col">{{ block.index }}#eBlock</td>
                            <td>{{ getAlarmLevelDesc(block.alarm_level) }}</td>
                            <td>{{ block.temp ?? '--' }}℃</td>
                            <td>{{ block.hum ?? '--' }}%</td>
                            <td>{{ block.co_conc ?? '--' }}ppm</td>
                            <td>{{ block.smoke_conc ?? '--' }}ppm</td>
                            <td>
                              <span :class="getCoAlarmClass(block.co_alarm)">{{ getCoAlarmDesc(block.co_alarm) }}</span>
                            </td>
                            <td>
                              <span :class="getCoAlarmClass(block.voc_alarm)">{{ getCoAlarmDesc(block.voc_alarm)
                              }}</span>
                            </td>
                            <td>
                              <span :class="getSprayingClass(block.spraying)">{{ getSprayingDesc(block.spraying)
                              }}</span>
                            </td>
                            <td>
                              <span :class="getManualClass(block.manual_warn)">{{ getManualDesc(block.manual_warn)
                              }}</span>
                            </td>
                            <td>
                              <span :class="getManualClass(block.manual_stop)">{{ getManualDesc(block.manual_stop)
                              }}</span>
                            </td>
                            <td>{{ block.soft_ver ?? '--' }}</td>
                            <td>{{ block.h2_conc ?? '--' }}ppm</td>
                            <td>
                              <span :class="getCoAlarmClass(block.h2_alarm)">{{ getH2AlarmDesc(block.h2_alarm) }}</span>
                            </td>
                            <td>
                              <div class="status-badge" :class="block.offline === 1 ? 'online' : 'offline'">
                                {{ block.offline === 1 ? '在线' : '离线' }}
                              </div>
                            </td>
                            <td class="text-center fixed-col-right">
                              <span class="action-link" style="color: #3A68FF !important;"
                                @click="openPackDetail(block)">查看</span>
                            </td>
                          </tr>
                        </tbody>
                      </v-table>
                      <!-- 暂无数据遮罩 -->
                      <div v-if="!elink.data.cabinet || elink.data.cabinet.length === 0" class="no-data-overlay">
                        暂无数据
                      </div>
                    </div>

                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>

        <!-- 右侧：设备树 -->
        <div class="right-sidebar" v-if="false">
          <!-- 搜索框 -->
          <div class="search-wrapper">
            <v-text-field v-model="searchText" placeholder="按设备名称/UUID搜索设备" rounded variant="outlined" density="compact"
              append-inner-icon="mdi-magnify" clearable hide-details class="search-input"></v-text-field>
          </div>

          <!-- 设备树 -->
          <div class="device-tree">
            <v-treeview :items="treeItems" :search="searchText" :custom-filter="filterDevice" item-value="id"
              item-title="name" activatable open-all density="compact" class="custom-treeview"
              @update:activated="handleTreeItemClick">
              <template v-slot:prepend="{ item }">
                <img v-if="item.type === 'root'" src="@/assets/images/station-monitor/detail-dialog/tree-root-icon.png"
                  style="width: 16px; height: 14px;" alt="根节点" loading="lazy" />
                <img v-else-if="item.type === 'station'"
                  src="@/assets/images/station-monitor/detail-dialog/tree-station-icon.png"
                  style="width: 16px; height: 14px;" alt="电站" loading="lazy" />
                <img v-else-if="item.type === 'category'"
                  src="@/assets/images/station-monitor/detail-dialog/tree-category-icon.png"
                  style="width: 16px; height: 14px;" alt="分类" loading="lazy" />
                <img v-else src="@/assets/images/station-monitor/detail-dialog/tree-device-icon.png"
                  style="width: 16px; height: 14px;" alt="设备" loading="lazy" />
              </template>
            </v-treeview>
          </div>
        </div>


        <DeviceTree :items="items" :loading="deviceListLoading" v-model:selectedId="selectedId" @select="onSelect" />
      </div>

      <!-- 关闭按钮 -->
      <v-btn icon size="small" variant="text" class="close-btn" @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </RightSheet>

  <!-- Pack 详情弹框 (新组件) -->
  <FirePackDetailDialog v-model="packDialogVisible" :block="currentBlock" @close="onPackClose" />

  <!-- 消防趋势图弹框 -->
  <FireChartDialog v-model="fireChartDialogVisible" :station-id="currentStationId" :elink-uuid="currentElinkUuid"
    :initial-field="currentChartField" />
</template>

<script setup>
import { ref, computed, watch, onUnmounted, shallowRef, nextTick } from 'vue'
import { ws_req } from '@/ws/wss-request'
import useUserStore from '@/store/modules/user'
import { useStationContext } from '../../composables/useStationContext'
import FirePackDetailDialog from './FirePackDetailDialog.vue'
import FireChartDialog from './FireChartDialog.vue'
import dayjs from 'dayjs'
import RightSheet from '@/components/RightSheet/index.vue'
import { useDeviceList } from '@/hooks/useDeviceList'
import DeviceTree from '../../tabs/EnergyStorageSystem/components/DeviceTree/index.vue'

const { items, loading: deviceListLoading, getDeviceTypeById, getELinkIdByEBlockId } = useDeviceList()
const selectedId = ref([])
const onSelect = (selectedItems) => {
  console.log(selectedItems)
  highlightedDevice.value = {
    type: getDeviceTypeById(selectedItems.id),
    id: selectedItems.id,
    elinkId: getELinkIdByEBlockId(selectedItems.id)
  }
  const element = document.querySelector(`[data-device-id="${selectedItems.id}"]`)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const searchText = ref('')
const loading = ref(false)
const fireData = shallowRef([])
// 存储折叠状态，key为 index, value为 true(折叠)
const collapsedPanels = ref({})
let wsTimer = null

// 使用 composable 获取电站上下文信息
const { stationId: currentStationId, stationName: currentStationName } = useStationContext()

// 树结构
const treeItems = shallowRef([])
const highlightedDevice = ref({ type: null, id: null, elinkId: null })

// Pack详情
const packDialogVisible = ref(false)
const currentBlock = ref(null)
const packList = ref([])

// 趋势图详情
const fireChartDialogVisible = ref(false)
const currentElinkUuid = ref('')
const currentChartField = ref('temp')

// 打开趋势图
const openChartDialog = (elink, field) => {
  if (!elink || !elink.uuid) {
    console.warn('eLink UUID missing', elink)
    return
  }
  currentElinkUuid.value = elink.uuid
  currentChartField.value = field
  fireChartDialogVisible.value = true
}


// 切换折叠状态
const togglePanel = (index) => {
  collapsedPanels.value[index] = !collapsedPanels.value[index]
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '--'
  return dayjs(time).format('YYYY-MM-DD HH:mm:ss')
}

// 状态描述映射
// 状态常量定义 (参考 monitor.fire.vue)
const AlarmLevels = { 0: '正常', 1: '一级预警', 2: '二级预警', 3: '三级预警' }
const CoAlarms = { 0: '正常', 1: '告警' } // 适用于 CO, VOC, H2, Smoke
const SprayingAlarms = { 0: '未喷洒', 1: '正在喷洒' }
const ManualStatus = { 0: '正常', 1: '按下' }
const OfflineStatus = { 0: '离线', 1: '在线' }
const PowerSupplyStatus = { 0: '市电', 1: '电池' }
const BatLowStatus = { 0: '电量正常', 1: '电量低' }
const LiquidLowStatus = { 0: '液位正常', 1: '液位低' }

// 状态描述映射
const getAlarmLevelDesc = (level) => {
  if (level === undefined || level === null) return '--'
  return AlarmLevels[level] || '--'
}

const getCoAlarmDesc = (status) => {
  return CoAlarms[status] || '--'
}

const getSprayingDesc = (status) => {
  return SprayingAlarms[status] ?? '--'
}

const getManualDesc = (status) => {
  return ManualStatus[status] ?? '--'
}

const getH2AlarmDesc = (status) => {
  return CoAlarms[status] ?? '--' // H2 使用相同的告警逻辑
}

const getOfflineDesc = (val) => OfflineStatus[val] ?? '--'
const getPowerSupplyDesc = (val) => PowerSupplyStatus[val] ?? '--'
const getBatLowDesc = (val) => BatLowStatus[val] ?? '--'
const getLiquidLowDesc = (val) => LiquidLowStatus[val] ?? '--'


const getCoAlarmClass = (status) => {
  return status === 1 ? 'text-red-accent-2' : 'text-grey-darken-1'
}

const getManualClass = (status) => {
  return status === 1 ? 'text-red-accent-2' : 'text-grey-darken-1'
}

const getSprayingClass = (status) => {
  return status === 1 ? 'text-success ' : 'text-grey-darken-1' // 喷洒通常是绿色(success)? monitor.fire.vue uses {0: 'normal', 1: 'success'}
}

// 获取Pack详情
const openPackDetail = async(block) => {
  currentBlock.value = block
  packDialogVisible.value = true
  
  await nextTick()
  dialogVisible.value = false
}

const onPackClose = () => {
  dialogVisible.value = true
}

// WS请求
const fetchFireData = async (recurs = true) => {
  clearTimeout(wsTimer)

  if (!currentStationId.value) return

  try {
    if (!ws_req.is_open && !ws_req.is_connecting) {
      const userStore = useUserStore()
      await userStore.getInfo()
    }

    const result = await ws_req.request('fire_sys', {
      stationId: currentStationId.value
    })

    if (result && Array.isArray(result)) {
      result.forEach(item => {
        if (!item.data) item.data = {}
      })
      fireData.value = result

      if (treeItems.value.length === 0 || treeItems.value[0]?.children?.length !== result.length) {
        buildTreeItems(result)
      }
    }
  } catch (e) {
    console.error('Fire Sys WS request failed:', e)
  }

  if (recurs && dialogVisible.value) {
    wsTimer = setTimeout(() => {
      fetchFireData(true)
    }, 5000)
  }
}

// 构建树结构
const buildTreeItems = (data) => {
  if (!data || !Array.isArray(data)) return

  const children = data.map((item, index) => {
    // 确保树节点名称显示 elink_name
    const arrayName = `${index + 1}#储能方阵`
    const elinkName = item.elink_name || `${index + 1}#能量链`
    // 使用 elink_id 如果存在，否则使用 index 作为唯一标识的一部分
    const uniqueElinkId = item.elink_id ?? index
    const blocks = item.data?.cabinet || []

    return {
      id: `array-${uniqueElinkId}`,
      name: arrayName, // 外层显示 "1#储能方阵"
      type: 'category',
      children: [{
        id: `elink-${uniqueElinkId}`,
        name: elinkName, // 内层显示 "1#能量链"
        type: 'elink',
        uuid: item.uuid,
        rawElinkId: uniqueElinkId
      }, ...blocks.map(b => ({
        // eblock ID 组合 elink ID 防止重复，使用 index 作为唯一标识，因为 eblock_id 可能重复或为空
        id: `eblock-${uniqueElinkId}-${b.index}`,
        name: `${b.index}#eBlock`,
        type: 'eblock',
        uuid: b.uuid,
        rawEblockId: b.eblock_id,
        rawElinkId: uniqueElinkId
      }))]
    }
  })

  treeItems.value = [
    {
      id: 'station-root',
      name: currentStationName.value,
      type: 'station',
      children: children
    }
  ]
}

// 树点击处理
const handleTreeItemClick = (selectedItems) => {
  let itemId = null
  if (Array.isArray(selectedItems)) {
    if (selectedItems.length === 0) return
    itemId = selectedItems[0]
  } else if (selectedItems) {
    itemId = selectedItems
  } else {
    return
  }

  let targetType = null
  let targetId = null
  let targetElinkId = null

  if (itemId.startsWith('elink-')) {
    targetType = 'elink'
    targetElinkId = itemId.replace('elink-', '') // String
    // 尝试转数字如果原 ID 是数字
    if (!isNaN(targetElinkId)) targetElinkId = Number(targetElinkId)
    targetId = targetElinkId
  } else if (itemId.startsWith('eblock-')) {
    targetType = 'eblock'
    const parts = itemId.split('-')
    // ID 格式: eblock-{elinkId}-{eblockId}
    if (parts.length >= 3) {
      targetElinkId = parts[1]
      targetId = parts.slice(2).join('-') // 重新组合剩余部分以防eblockId含横杠
      if (!isNaN(targetElinkId)) targetElinkId = Number(targetElinkId)
      // eblockId 可能是数字或字符串，保持原样或尝试转换
      // 考虑到 table data 使用原始值，这里尽可能保持类型一致
      // 但是从 string split 出来肯定是 string。
      // 在比较时使用 == (loose equality)
    }
  }

  if (targetType) {
    let arrayIndex = -1

    if (targetElinkId !== null) {
      arrayIndex = fireData.value.findIndex((item, idx) => {
        const uid = item.elink_id ?? idx
        return uid == targetElinkId
      })
    }

    if (arrayIndex !== -1) {
      collapsedPanels.value[arrayIndex] = false; // 展开
    }

    // 设置高亮
    highlightedDevice.value = {
      type: targetType,
      id: targetId,
      elinkId: targetElinkId
    }

    setTimeout(() => {
      let selector = ''
      if (targetType === 'elink') {
        selector = `#fire-array-${targetElinkId}` // 滚动到外层面板
      } else if (targetType === 'eblock') {
        // 限定在特定的 array panel 内寻找，使用 data-index 定位
        selector = `#fire-array-${targetElinkId} tr[data-index="${targetId}"]`
      }

      if (selector) {
        const element = document.querySelector(selector)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }


    }, 300)

    // 移除自动清除高亮，保持选中状态，直到点击下一个
    // setTimeout(() => {
    //   highlightedDevice.value = { type: null, id: null, elinkId: null }
    // }, 3000)
  }
}

const filterDevice = (value, search, item) => {
  if (!search) return true
  const searchLower = search.toLowerCase()
  const text = (value || '').toLowerCase()
  const rawItem = item.raw || item
  const uuid = (rawItem.uuid || '').toLowerCase()
  return text.includes(searchLower) || uuid.includes(searchLower)
}

const closeDialog = () => {
  dialogVisible.value = false
}

watch(dialogVisible, (newVal) => {
  if (newVal && currentStationId.value) {
    loading.value = true
    fetchFireData(true).finally(() => { loading.value = false })
  } else {
    clearTimeout(wsTimer)
  }
})

watch(currentStationId, (newVal) => {
  if (newVal && dialogVisible.value) {
    fetchFireData(true)
  }
})

onUnmounted(() => {
  clearTimeout(wsTimer)
})
</script>

<style scoped lang="scss">
.fire-detail-dialog,
.fire-pack-detail-dialog {
  :deep(.v-overlay__content) {
    width: 100vw !important;
    max-width: 1920px !important;
    height: 950px !important;
    margin: 0 auto !important;
    border-radius: 30px 30px 0 0 !important;
  }
}

.dialog-wrapper {
  position: relative;
  width: 100%;
  height: 100vh;
  padding: 20px 24px 0;
  background-color: #fff !important;
  background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
  background-size: cover;
  border-radius: 30px 30px 0 0 !important;
  display: flex;
  flex-direction: column;
}

.dialog-title {
  // height: 80px;
  display: flex;
  align-items: center;
  padding: 0 40px 0 24px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  position: relative;
  flex-shrink: 0;
  margin-bottom: 20;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
}

.dialog-main {
  display: flex;
  flex-direction: row-reverse;
  gap: 16px;
  height: calc(100% - 143px);
  /* 143 = 80header + padding? Adjust as needed matching ClusterDetail styling context */
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 20px;
  margin-bottom: 20px;
  overflow: hidden;
}

/* Reuse table styles from ClusterDetailTable */
.card-bg {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
}

.card-bd {
  border: 1px solid #fff;
}

.custom-table {
  :deep(th) {
    font-size: 14px !important;
    height: 48px !important;
    border-bottom: 1px solid #E0E4E7 !important;
    background-color: transparent !important;
  }

  :deep(td) {
    font-size: 14px !important;
    height: 52px !important;
    border-bottom: 1px solid #E0E4E7 !important;
  }
}

.header-pointer {
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.text-normal {
  color: #444a6d;
  /* default text color */
}

.dialog-main {
  display: flex;
  gap: 30px;
  height: calc(100% - 83px);
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 20px;
}

.left-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding-right: 5px;
}

/* 方阵面板 */
.fire-array-panel {
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 16px;
}

.array-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 22px;
  cursor: pointer;
  user-select: none;
  background: #fff;
  // border-bottom: 1px solid #f0f0f0;

  &:hover {
    background-color: rgba(246, 251, 255, 0.5);
  }
}

.array-title {
  font-size: 16px;
  color: rgba(46, 46, 46, 1);
  font-weight: bold;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-time {
  font-size: 12px;
  color: #666;
}

.collapse-icon {
  width: 11px;
  height: 6px;
  transition: transform 0.3s ease;

  &.expanded {
    transform: rotate(180deg);
  }
}

.array-content-wrapper {
  padding: 20px 22px;
}

.array-content {
  display: flex;
  gap: 18px;
  align-items: stretch;
}

/* 左侧 eLink 卡片 */
.elink-info-card {
  width: 280px;
  /* 加宽以适应双列布局 */
  flex-shrink: 0;
  background: #fff;
  /* 改为白色底，或者保持设计稿可能有的极浅色 */
  border: 1px solid #F4F7FC;
  border-radius: 8px;
  // padding: 12px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s ease;

  &.highlight-device {
    animation: highlightBlink 0.5s ease-in-out 6;
  }
}

@keyframes highlightBlink {

  0%,
  100% {
    border-color: rgba(244, 247, 252, 1);
    box-shadow: none;
  }

  50% {
    border-color: rgba(58, 104, 255, 1);
    box-shadow: 0 0 12px rgba(58, 104, 255, 0.5);
  }
}

.elink-header {
  height: 48px;
  margin-bottom: 12px;
  background: rgba(239, 244, 251, 0.7);
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  align-items: center;

  .elink-icon-title {
    display: flex;
    align-items: center;

    .header-icon-box {
      margin-right: 8px;
      display: flex;
      align-items: center;
    }

    .elink-name {
      font-weight: 400;
      font-size: 14px;
      color: rgba(46, 46, 46, 1);
    }
  }
}

.elink-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  /* 间距 */
  margin-bottom: 12px;
}

/* 每个信息项的小盒子 */
.info-item-box {
  background: rgba(244, 247, 252, 0.6);
  /* 浅灰背景 */
  border-radius: 6px;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 64px;

  &.empty-box {
    background: transparent;
  }

  .label {
    font-size: 12px;
    color: rgba(46, 46, 46, 1);
    font-weight: 300;
    margin-bottom: 8px;
  }

  .value {
    font-size: 14px;
    color: rgba(46, 46, 46, 1);
    display: flex;
    align-items: center;
    font-weight: 400;

    .text-value {
      margin-left: 2px;
    }
  }
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;

  &.success {
    background-color: #52C41A;
  }

  &.error {
    background-color: #FF4D4F;
  }

  &.normal {
    background-color: #ccc;
  }
}

.no-data-overlay {
  position: absolute;
  top: 48px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  color: #999;
  font-size: 14px;
  z-index: 5;
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
}

.version-info {
  background: transparent;
  padding: 0;
  margin-top: 16px;

  .version-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding: 0 14px;
    height: 42px;
    background: rgba(244, 247, 252, 0.6);
    border-radius: 5px;

    &:last-child {
      margin-bottom: 0;
    }

    .version-label {
      color: rgba(46, 46, 46, 1);
      font-weight: 300;
      font-size: 12px;
    }

    .version-value {
      color: rgba(46, 46, 46, 1);
      font-weight: 400;
      font-size: 14px;
    }
  }
}

/* 右侧 eBlock 表格 */
.eblock-table-container {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  // border: 1px solid #F4F7FC;
  overflow: hidden;

  :deep(.v-table) {
    .v-table__wrapper {
      border-radius: 8px;
    }
  }
}

.custom-table {
  font-size: 14px;

  thead th {
    background-color: #fff !important;
    /* 表头白色或极浅色 */
    color: rgba(150, 165, 184, 1) !important;
    font-weight: 400 !important;
    font-size: 12px;
    white-space: nowrap;
    height: 48px !important;
    border-bottom: none !important;
    box-shadow: none !important;
  }

  tbody td {
    height: 52px !important;
    white-space: nowrap;
    color: #444A6D !important;
    border-bottom: 1px solid rgba(237, 242, 246, 1) !important;

    span {
      // color: #444A6D !important;
    }
  }

  /* 固定首列 */
  thead th.fixed-col,
  tbody td.fixed-col {
    position: sticky !important;
    left: 0 !important;
    z-index: 2;
    /* 内容层级 */
    background-color: #fff !important;
    box-shadow: 7px 2px 11.8px 0 rgba(0, 0, 0, 0.04) !important;
    border-right: none;
  }

  /* 固定尾列 */
  thead th.fixed-col-right,
  tbody td.fixed-col-right {
    position: sticky !important;
    right: 0 !important;
    z-index: 2;
    background-color: #fff !important;
    box-shadow: -7px 2px 11.8px 0 rgba(0, 0, 0, 0.04) !important;
    border-left: none;
  }

  /* 提升固定列表头的层级 */
  thead th.fixed-col,
  thead th.fixed-col-right {
    z-index: 3 !important;
    /* 表头高于内容 */
  }
}

.status-badge {
  padding: 4px 12px;
  border-radius: 37px;
  font-size: 14px;
  display: inline-block;

  &.online {
    background: #DDF3D6;
    color: #36B60C;
  }

  &.offline {
    background: #F4F7FC;
    color: #6B6C6D;
  }
}

.action-link {
  color: #3A68FF !important;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.highlight-row {
  background-color: #E6F7FF !important;
}

.right-sidebar {
  width: 319px;
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.search-wrapper {
  position: relative;
  margin-bottom: 26px;
}

.device-tree {
  flex: 1;
  overflow-y: auto;
}

/* Animation */
.panel-expand-enter-active,
.panel-expand-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  opacity: 1;
  overflow: hidden;
}

.panel-expand-enter-from,
.panel-expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
